import { useCallback } from "react";

import { t } from "@/i18n-js/instance";
import { useCurrentPlanApi } from "@/react/components/SettingsApp/Plan/hooks/useCurrentPlanApi";
import { targetDowngradingPlanName } from "@/react/helpers/planHelpers";
import { useScheduleDowngradesApi } from "@/react/hooks/useScheduleDowngradesApi";

import { SCHEDULED_DOWNGRADE_TYPES } from "../constants";
import type { CancelScheduledDowngradeModalSource } from "../types";
import { CANCEL_SCHEDULED_DOWNGRADE_SOURCES } from "../types";

interface CancelDowngradeScheduledModalCopies {
  confirmText: string;
  description: string;
  title: string;
}

interface UseCancelDowngradeScheduledHandlerProps {
  source: string;
}

const getValidCancellationSource = (source: string) => {
  const sourceAsScheduledModalSource =
    source as CancelScheduledDowngradeModalSource;
  return Object.values(CANCEL_SCHEDULED_DOWNGRADE_SOURCES).includes(
    sourceAsScheduledModalSource,
  )
    ? sourceAsScheduledModalSource
    : CANCEL_SCHEDULED_DOWNGRADE_SOURCES.FALLBACK_SOURCE;
};

export const useCancelDowngradeScheduledHandler = ({
  source,
}: UseCancelDowngradeScheduledHandlerProps) => {
  const { currentPlan, refetchCurrentPlan } = useCurrentPlanApi();
  const { cancelScheduledDowngrade } = useScheduleDowngradesApi();
  const scheduleCancellationSource = getValidCancellationSource(source);
  const isScheduledDowngradeActive =
    currentPlan?.is_community_hub_downgrade_scheduled;

  const scheduledDowngradeMetadata = currentPlan?.scheduled_downgrade_metadata;
  const isPlanDowngrade = scheduledDowngradeMetadata?.plan_downgrade;
  const nextPlan = scheduledDowngradeMetadata?.next_plan;
  const scheduledDowngradeType = isPlanDowngrade
    ? SCHEDULED_DOWNGRADE_TYPES.PLAN_DOWNGRADE
    : SCHEDULED_DOWNGRADE_TYPES.INTERVAL_DOWNGRADE;

  const handleCancelScheduledDowngrade = useCallback(
    (onSuccess?: () => void) => {
      cancelScheduledDowngrade(undefined, {
        onSuccess: () => {
          void refetchCurrentPlan();
          if (onSuccess) {
            onSuccess();
          }
        },
      });
    },
    [cancelScheduledDowngrade, refetchCurrentPlan],
  );

  const getModalConfig =
    useCallback((): CancelDowngradeScheduledModalCopies => {
      const baseI18nRoot = `pricing_plans.cancel_scheduled_downgrade.${scheduleCancellationSource}.${scheduledDowngradeType}.cancel_modal`;
      if (
        scheduleCancellationSource ===
          CANCEL_SCHEDULED_DOWNGRADE_SOURCES.SCHEDULED_DOWNGRADE_BANNER ||
        scheduleCancellationSource ===
          CANCEL_SCHEDULED_DOWNGRADE_SOURCES.FALLBACK_SOURCE
      ) {
        return {
          title: t([baseI18nRoot, "title"]),
          confirmText: t([baseI18nRoot, "confirm_button"]),
          description: t([baseI18nRoot, "description"], {
            target_plan_name: targetDowngradingPlanName(
              scheduleCancellationSource,
              nextPlan,
            ),
          }),
        };
      }
      return {
        title: t([baseI18nRoot, "title"]),
        confirmText: t([baseI18nRoot, "confirm_button"]),
        description: t([baseI18nRoot, "description_on_changing_plan"], {
          target_plan_name: targetDowngradingPlanName(
            scheduleCancellationSource,
            nextPlan,
          ),
        }),
      };
    }, [scheduleCancellationSource, scheduledDowngradeType, nextPlan]);

  return {
    isScheduledDowngradeActive,
    handleCancelScheduledDowngrade,
    getModalConfig,
  };
};
